import type { ConsumerApiClient } from '../client';

export interface PaymentMethod {
  id: string;
  type: 'card';
  last4: string;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
  nickname?: string;
}

export interface StaxCheckoutRequest {
  paymentMethod: 'card' | 'wallet' | 'split';
  paymentMethodId?: string;
  walletAmountCents?: number;
  shippingOption?: 'standard' | 'premium';
  saveCard?: boolean;
  cardLastFour?: string;
  cardBrand?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  noteText?: string;
}

export interface StaxCheckoutResponse {
  orderId: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  totalCents: number;
  walletAmountCents: number;
  cardAmountCents: number;
  pelipointsEarned: number;
  staxTransactionId: string | null;
}

export interface TokenizeCardRequest {
  cardNumber: string;
  expMonth: string;
  expYear: string;
  cvv: string;
  cardholderName: string;
}

export interface TokenizeCardResponse {
  paymentMethodId: string;
  cardLastFour: string;
  cardBrand: string;
  cardExpMonth?: number;
  cardExpYear?: number;
}

export interface SavePaymentMethodRequest {
  paymentMethodId: string;
  cardLastFour?: string;
  cardBrand?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  nickname?: string;
  isDefault?: boolean;
}

export interface PaymentsApi {
  getMethods(): Promise<{ methods: PaymentMethod[] }>;
  /** Stax-powered checkout — charges card and creates order from cart */
  staxCheckout(data: StaxCheckoutRequest): Promise<StaxCheckoutResponse>;
  /** Tokenize a raw credit card via the backend Stax integration. Returns a paymentMethodId we can hand to staxCheckout. */
  tokenizeCard(data: TokenizeCardRequest): Promise<TokenizeCardResponse>;
  /** Legacy payment processing */
  processPayment(
    orderId: string,
    paymentMethod: 'wallet' | 'card',
    cardToken?: string,
  ): Promise<any>;
  savePaymentMethod(data: SavePaymentMethodRequest): Promise<PaymentMethod>;
  removePaymentMethod(id: string): Promise<void>;
  setDefaultPaymentMethod(id: string): Promise<PaymentMethod>;
}

export function createPaymentsApi(client: ConsumerApiClient): PaymentsApi {
  return {
    getMethods() {
      return client.get('/payments/methods');
    },

    staxCheckout(data) {
      return client.post('/payments/checkout', data);
    },

    tokenizeCard(data) {
      return client.post('/payments/tokenize-card', data);
    },

    processPayment(orderId, paymentMethod, cardToken) {
      return client.post('/payments/pay', { orderId, paymentMethod, cardToken });
    },

    savePaymentMethod(data) {
      return client.post('/payments/methods', data);
    },

    removePaymentMethod(id) {
      return client.delete(`/payments/methods/${id}`);
    },

    setDefaultPaymentMethod(id) {
      return client.put(`/payments/methods/${id}/default`);
    },
  };
}
